import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { useExternalBusyWeek } from "@/hooks/calendar/useExternalBusy";
import { bookingMinutes, busyLabel, busyOverlaps } from "@/lib/calendar/externalBusy";
import { hmFromMinutes } from "@/lib/time";
import { getWeekStart } from "@/lib/utils";

/**
 * The create and edit modals' soft warning (boona docs/codebase/google-calendar/README.md §11): the
 * chosen master and time sit on busy time from her own calendar or Booksy. It only informs — the
 * admin keeps the right to book over it, and nothing here touches the save. It reads the journal's
 * cached week of busy blocks (useExternalBusyWeek), so it costs no request for the week on screen.
 */
export function BusyOverlapWarning({
  staffId,
  date,
  startTime,
  endTime,
}: {
  staffId: number | null | undefined;
  /** The salon-local day, YYYY-MM-DD. */
  date: string | null | undefined;
  /** Salon-local "HH:mm". */
  startTime: string | null | undefined;
  endTime: string | null | undefined;
}) {
  const { t } = useTranslation();
  const day = date && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : null;
  const { data: blocks } = useExternalBusyWeek(day ? getWeekStart(day) : null);

  const booking = bookingMinutes(startTime, endTime);
  const hits = booking ? busyOverlaps(blocks, { staffId, date: day, ...booking }) : [];
  if (hits.length === 0) return null;

  const where = hits
    .map((block) => `${busyLabel(block.provider, t)} ${hmFromMinutes(block.startMin)}–${hmFromMinutes(block.endMin)}`)
    .join(", ");

  return (
    <div role="status" className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
      <div>
        <div>{t("calendar.busy.booking_warning", "This time is busy in the master's calendar")}</div>
        <div className="text-amber-700/80">{where}</div>
      </div>
    </div>
  );
}
